/**
 * coverColors — the two tones a cover's tile is drawn with.
 *
 * A shade already carries its colour in the hub: the 3D model slot's RAL tone
 * (`settings.model3d.library.prefs.color`, the one the Home 3D view paints the
 * real blind with). The card reads that same code, so the lamella on the tile is
 * the lamella on the house — the owner: *"would be good to have colors. window
 * frame color, lamella color,.."*.
 *
 * The window's frame has no code of its own: it is the opening's own sash, so it
 * takes the cover's tone lifted toward the room's white — a dark anthracite
 * blind then has a visibly lighter frame instead of one flat silhouette, and a
 * white blind keeps a frame that still reads against the card.
 *
 * The shade itself is lifted partway toward that frame: on a dark card an
 * anthracite RAL read as a black rectangle with no fabric in it (owner,
 * 27.10.2026: *"the shade is too dark (on dark theme).. make it brighter, more
 * like frame color"*). The lift keeps the hue and leaves a white blind white.
 */

import { parseDeviceModel, ralRgb, DEFAULT_RAL_CODE } from './bimDeviceModel';

export interface CoverColors {
  /** The lamella / fabric — the device's own RAL tone. */
  material: string;
  /** The window's sash and reveal, a lighter step off the same tone. */
  frame: string;
}

/** A `[0..1, 0..1, 0..1]` triple as `#rrggbb`. */
function hexOf(rgb: readonly number[]): string {
  const channel = (value: number) =>
    Math.max(0, Math.min(255, Math.round(value * 255)))
      .toString(16)
      .padStart(2, '0');
  return `#${channel(rgb[0] ?? 0)}${channel(rgb[1] ?? 0)}${channel(rgb[2] ?? 0)}`;
}

/** `a` mixed toward `b` by `amount` (0 = a, 1 = b). */
function mixHex(a: string, b: string, amount: number): string {
  const parse = (hex: string): [number, number, number] => {
    const value = hex.replace('#', '');
    const full = value.length === 3 ? [...value].map((c) => c + c).join('') : value;
    return [
      parseInt(full.slice(0, 2), 16) || 0,
      parseInt(full.slice(2, 4), 16) || 0,
      parseInt(full.slice(4, 6), 16) || 0,
    ];
  };
  const [ar, ag, ab] = parse(a);
  const [br, bg, bb] = parse(b);
  const t = Math.max(0, Math.min(1, amount));
  return hexOf([(ar + (br - ar) * t) / 255, (ag + (bg - ag) * t) / 255, (ab + (bb - ab) * t) / 255]);
}

/** The sash tone every cover's frame is lifted toward. */
const ROOM_WHITE = '#eef1f6';

/**
 * The tones a cover's tile draws with, off the device's own stored slot. A cover
 * that never had a model attached gets the library's default RAL tone — the
 * colour the 3D view would build for it too.
 */
export function coverColorsOf(device: unknown): CoverColors {
  const settings =
    typeof device === 'object' && device !== null ? (device as { settings?: unknown }).settings : undefined;
  const model = settings ? parseDeviceModel(settings) : null;
  const code = model?.library?.prefs.color ?? DEFAULT_RAL_CODE;
  const base = hexOf(ralRgb(code));
  const frame = mixHex(base, ROOM_WHITE, 0.45);
  // The shade sits close to the frame, a step below it: a dark anthracite RAL
  // used to read as a black rectangle on a dark card, with no fabric in it.
  return { material: mixHex(base, frame, 0.7), frame };
}
